// Servicio para API Football
// Documentación: https://www.api-football.com/documentation-v3

// NOTA: API Football no permite CORS desde el navegador
// Para desarrollo, usamos un proxy CORS. En producción, usar Cloud Functions
const USE_CORS_PROXY = import.meta.env.DEV // Solo en desarrollo
const CORS_PROXY = 'https://corsproxy.io/?'
const API_BASE_URL = 'https://v3.football.api-sports.io'
const API_KEY = import.meta.env.VITE_API_FOOTBALL_KEY

// Verificar que la API key esté configurada
if (!API_KEY) {
  console.error('❌ VITE_API_FOOTBALL_KEY no está configurada en el archivo .env')
} else {
  console.log('✅ API Football key configured:', API_KEY.substring(0, 8) + '...')
  if (USE_CORS_PROXY) {
    console.log('⚠️  Usando proxy CORS para desarrollo')
  }
}

// Construir URL con o sin proxy
const buildUrl = (endpoint) => {
  const fullUrl = `${API_BASE_URL}${endpoint}`
  return USE_CORS_PROXY ? `${CORS_PROXY}${fullUrl}` : fullUrl
}

// Headers requeridos
const getHeaders = () => ({
  'x-apisports-key': API_KEY,
  'Content-Type': 'application/json'
})

// IDs de ligas importantes
export const LEAGUES = {
  LIGA_MX: 262,
  MUNDIAL: 1, // FIFA World Cup
  CHAMPIONS_LEAGUE: 2 // UEFA Champions League (ID 2 en API-FOOTBALL)
}

// Temporadas
export const SEASONS = {
  2024: 2024,
  2023: 2023,
  2022: 2022
}

/**
 * Obtener información de una liga
 * @param {number} leagueId - ID de la liga
 * @param {number} season - Año de la temporada
 */
export const getLeagueInfo = async (leagueId = LEAGUES.LIGA_MX, season = 2024) => {
  try {
    const endpoint = `/leagues?id=${leagueId}&season=${season}`
    const response = await fetch(
      buildUrl(endpoint),
      {
        method: 'GET',
        headers: getHeaders()
      }
    )

    if (!response.ok) {
      throw new Error(`API Football Error: ${response.status}`)
    }

    const data = await response.json()
    
    // Log completo para debugging
    console.log('League info response:', {
      endpoint,
      results: data.results,
      hasData: data.response && data.response.length > 0,
      data: data.response?.[0]
    })
    
    // Verificar rate limit
    console.log('Rate Limit:', {
      remaining: response.headers.get('x-ratelimit-requests-remaining'),
      limit: response.headers.get('x-ratelimit-requests-limit')
    })

    if (data.results === 0) {
      console.warn(`⚠️ League ${leagueId} not found for season ${season}`)
      console.log('💡 Tip: Liga MX may use different season years (e.g., Apertura 2025, Clausura 2026)')
    }

    return data.response[0] || null
  } catch (error) {
    console.error('Error fetching league info:', error)
    throw error
  }
}

/**
 * Obtener las jornadas disponibles de una liga
 * @param {number} leagueId - ID de la liga
 * @param {number} season - Año de la temporada
 * @param {boolean} current - Solo jornada actual
 */
export const getRounds = async (leagueId = LEAGUES.LIGA_MX, season = 2024, current = false) => {
  try {
    let endpoint = `/fixtures/rounds?league=${leagueId}&season=${season}`
    if (current) {
      endpoint += '&current=true'
    }

    console.log('Fetching rounds for:', { leagueId, season, endpoint })
    const response = await fetch(buildUrl(endpoint), {
      method: 'GET',
      headers: getHeaders()
    })

    if (!response.ok) {
      throw new Error(`API Football Error: ${response.status}`)
    }

    const data = await response.json()
    
    // Check for API errors
    if (data.errors && Object.keys(data.errors).length > 0) {
      const errorMsg = Object.values(data.errors)[0]
      throw new Error(errorMsg)
    }
    
    console.log('Rounds response:', data)
    
    // Si no hay resultados, verificar si la liga existe para esa temporada
    if (data.results === 0) {
      console.warn(`⚠️ No rounds found for league ${leagueId}, season ${season}`)
      console.log('🔍 Checking league info...')
      
      // Verificar información de la liga para esa temporada
      try {
        const leagueInfo = await getLeagueInfo(leagueId, season)
        console.log('League info:', leagueInfo)
      } catch (err) {
        console.error('Could not fetch league info:', err)
      }
    }
    
    return data.response || []
  } catch (error) {
    console.error('Error fetching rounds:', error)
    throw error
  }
}

/**
 * Obtener partidos de una jornada específica
 * @param {number} leagueId - ID de la liga
 * @param {number} season - Año de la temporada
 * @param {string} round - Nombre de la jornada (ej: "Regular Season - 15")
 */
export const getFixturesByRound = async (leagueId = LEAGUES.LIGA_MX, season = 2024, round) => {
  try {
    const endpoint = `/fixtures?league=${leagueId}&season=${season}&round=${encodeURIComponent(round)}`
    const response = await fetch(
      buildUrl(endpoint),
      {
        method: 'GET',
        headers: getHeaders()
      }
    )

    if (!response.ok) {
      throw new Error(`API Football Error: ${response.status}`)
    }

    const data = await response.json()
    return data.response || []
  } catch (error) {
    console.error('Error fetching fixtures:', error)
    throw error
  }
}

/**
 * Obtener partidos por fecha
 * @param {string} date - Fecha en formato YYYY-MM-DD
 * @param {number} leagueId - ID de la liga (opcional)
 */
export const getFixturesByDate = async (date, leagueId = null) => {
  try {
    let endpoint = `/fixtures?date=${date}`
    if (leagueId) {
      endpoint += `&league=${leagueId}`
    }

    const response = await fetch(buildUrl(endpoint), {
      method: 'GET',
      headers: getHeaders()
    })

    if (!response.ok) {
      throw new Error(`API Football Error: ${response.status}`)
    }

    const data = await response.json()
    return data.response || []
  } catch (error) {
    console.error('Error fetching fixtures by date:', error)
    throw error
  }
}

/**
 * Obtener un partido por ID
 * @param {number} fixtureId - ID del partido
 */
export const getFixtureById = async (fixtureId) => {
  try {
    const endpoint = `/fixtures?id=${fixtureId}`
    const response = await fetch(
      buildUrl(endpoint),
      {
        method: 'GET',
        headers: getHeaders()
      }
    )

    if (!response.ok) {
      throw new Error(`API Football Error: ${response.status}`)
    }

    const data = await response.json()
    return data.response[0] || null
  } catch (error) {
    console.error('Error fetching fixture:', error)
    throw error
  }
}

/**
 * Obtener múltiples partidos por sus IDs
 * @param {string[]} fixtureIds - Array de IDs de partidos
 */
export const getFixturesByIds = async (fixtureIds) => {
  try {
    if (!fixtureIds || fixtureIds.length === 0) {
      return []
    }

    // API Football permite obtener múltiples fixtures con ids separados por guiones
    // Ej: /fixtures?ids=1-2-3
    const idsString = fixtureIds.join('-')
    const endpoint = `/fixtures?ids=${idsString}`
    
    const response = await fetch(
      buildUrl(endpoint),
      {
        method: 'GET',
        headers: getHeaders()
      }
    )

    if (!response.ok) {
      throw new Error(`API Football Error: ${response.status}`)
    }

    const data = await response.json()
    const fixtures = data.response || []
    
    // Formatear los partidos
    return fixtures.map(formatFixture)
  } catch (error) {
    console.error('Error fetching fixtures by IDs:', error)
    // Si hay error, intentar obtenerlos uno por uno
    console.log('Attempting to fetch fixtures individually...')
    const promises = fixtureIds.map(id => getFixtureById(id))
    const results = await Promise.allSettled(promises)
    const fixtures = results
      .filter(r => r.status === 'fulfilled' && r.value)
      .map(r => formatFixture(r.value))
    return fixtures
  }
}

/**
 * Obtener partidos en vivo
 * @param {number} leagueId - ID de la liga (opcional)
 */
export const getLiveFixtures = async (leagueId = null) => {
  try {
    let endpoint = `/fixtures?live=all`
    if (leagueId) {
      endpoint = `/fixtures?live=${leagueId}`
    }

    const response = await fetch(buildUrl(endpoint), {
      method: 'GET',
      headers: getHeaders()
    })

    if (!response.ok) {
      throw new Error(`API Football Error: ${response.status}`)
    }

    const data = await response.json()
    return data.response || []
  } catch (error) {
    console.error('Error fetching live fixtures:', error)
    throw error
  }
}

/**
 * Obtener equipos de una liga
 * @param {number} leagueId - ID de la liga
 * @param {number} season - Año de la temporada
 */
export const getTeams = async (leagueId = LEAGUES.LIGA_MX, season = 2026) => {
  try {
    const endpoint = `/teams?league=${leagueId}&season=${season}`
    const response = await fetch(
      buildUrl(endpoint),
      {
        method: 'GET',
        headers: getHeaders()
      }
    )

    if (!response.ok) {
      throw new Error(`API Football Error: ${response.status}`)
    }

    const data = await response.json()
    return data.response || []
  } catch (error) {
    console.error('Error fetching teams:', error)
    throw error
  }
}

/**
 * Formatear fixture para uso en la aplicación
 * @param {object} fixture - Fixture de API Football
 */
export const formatFixture = (fixture) => {
  return {
    id: fixture.fixture.id.toString(), // Siempre string para Firestore
    apiId: fixture.fixture.id,
    date: new Date(fixture.fixture.date),
    venue: fixture.fixture.venue?.name || 'Por definir',
    status: fixture.fixture.status.short,
    teams: {
      home: {
        id: fixture.teams.home.id,
        name: fixture.teams.home.name,
        logo: fixture.teams.home.logo
      },
      away: {
        id: fixture.teams.away.id,
        name: fixture.teams.away.name,
        logo: fixture.teams.away.logo
      }
    },
    score: {
      home: fixture.goals.home,
      away: fixture.goals.away
    },
    league: {
      id: fixture.league.id,
      name: fixture.league.name,
      round: fixture.league.round
    }
  }
}

/**
 * Obtener jornada actual
 * @param {number} leagueId - ID de la liga
 * @param {number} season - Año de la temporada
 */
export const getCurrentRound = async (leagueId = LEAGUES.LIGA_MX, season = 2026) => {
  try {
    const rounds = await getRounds(leagueId, season, true)
    return rounds[0] || null
  } catch (error) {
    console.error('Error fetching current round:', error)
    return null
  }
}

/**
 * Verificar el estado de la API key y consumo
 */
export const checkAPIStatus = async () => {
  try {
    const endpoint = '/status'
    const response = await fetch(buildUrl(endpoint), {
      method: 'GET',
      headers: getHeaders()
    })

    if (!response.ok) {
      throw new Error(`API Football Error: ${response.status}`)
    }

    const data = await response.json()
    return data.response
  } catch (error) {
    console.error('Error checking API status:', error)
    throw error
  }
}

/**
 * Obtener resultados finales de partidos por sus IDs
 * @param {Array<number>} fixtureIds - Array de IDs de partidos
 * @returns {Array} - Array de partidos con resultados
 */
/**
 * Obtener resultados de fixtures usando IDs (requiere plan de paga)
 * @param {Array<number>} fixtureIds - IDs de los fixtures
 */
export const getFixtureResults = async (fixtureIds) => {
  try {
    if (!fixtureIds || fixtureIds.length === 0) {
      return []
    }

    // La API permite consultar múltiples fixtures separados por guiones (plan de paga)
    const ids = fixtureIds.join('-')
    const endpoint = `/fixtures?ids=${ids}`
    
    console.log('Fetching results for fixtures:', fixtureIds)
    console.log('📡 Making request to:', `${API_BASE_URL}${endpoint}`)
    
    const response = await fetch(buildUrl(endpoint), {
      method: 'GET',
      headers: getHeaders()
    })

    if (!response.ok) {
      throw new Error(`API Football Error: ${response.status}`)
    }

    const data = await response.json()
    
    // Log completo de la respuesta
    console.log('🔍 Full API Response:', {
      results: data.results,
      errors: data.errors,
      total: data.response?.length || 0,
      paging: data.paging
    })
    
    // Si hay errores en la API, mostrarlos claramente
    if (data.errors && Object.keys(data.errors).length > 0) {
      console.error('❌ API Football Errors:', data.errors)
      throw new Error(Object.values(data.errors)[0])
    }
    
    // Log detallado de fixtures
    console.log('API Response:', {
      total: data.response?.length || 0,
      fixtures: data.response?.map(f => ({
        id: f.fixture.id,
        status: f.fixture.status.short,
        statusLong: f.fixture.status.long,
        date: f.fixture.date,
        home: f.teams.home.name,
        away: f.teams.away.name
      })) || []
    })
    
    if (!data.response || data.response.length === 0) {
      console.warn('⚠️ API returned no fixtures')
      return []
    }
    
    // Filtrar solo partidos finalizados
    const finishedFixtures = data.response.filter(fixture => 
      fixture.fixture.status.short === 'FT' || 
      fixture.fixture.status.short === 'AET' || 
      fixture.fixture.status.short === 'PEN'
    )

    console.log(`Found ${finishedFixtures.length} finished fixtures out of ${fixtureIds.length} requested`)
    
    const formattedResults = finishedFixtures.map(fixture => ({
      ...formatFixture(fixture),
      id: fixture.fixture.id.toString(), // Asegurar que sea string (después del spread para sobrescribir)
      status: fixture.fixture.status.short,
      result: {
        home: fixture.goals.home,
        away: fixture.goals.away,
        winner: fixture.teams.home.winner === true ? 'home' : 
                fixture.teams.away.winner === true ? 'away' : 'draw'
      }
    }))
    
    console.log('📤 Returning results:', formattedResults.map(r => ({ id: r.id, type: typeof r.id })))
    return formattedResults
  } catch (error) {
    console.error('Error fetching fixture results:', error)
    throw error
  }
}

export default {
  LEAGUES,
  SEASONS,
  getLeagueInfo,
  getRounds,
  getFixturesByRound,
  getFixturesByDate,
  getFixtureById,
  getLiveFixtures,
  getTeams,
  formatFixture,
  getFixturesByIds,
  getCurrentRound,
  checkAPIStatus,
  getFixtureResults
}
